import { Controller } from "@hotwired/stimulus"
import { initializePaddle, type Paddle, type PaddleEventData } from "@paddle/paddle-js"
import { errorHandler } from "@/services/error_handler_service"

// Customer object for Paddle checkout
// Either contains existing customer ID or email for new customers
interface PaddleCustomer {
  id?: string
  email?: string
}

// Connects to data-controller="paddle-checkout"
export default class extends Controller {
  static override values = {
    clientToken: String,
    customerId: String,  // User's existing paddle_customer_id (if any)
    email: String        // User's email address
  }

  declare clientTokenValue: string
  declare customerIdValue: string
  declare emailValue: string
  declare readonly hasCustomerIdValue: boolean
  private paddle: Paddle | undefined

  override async connect() {
    await this.initializePaddle()
  }

  private showError(message: string) {
    // Dispatch event that can be handled by parent components
    this.element.dispatchEvent(new CustomEvent('paddle:error', {
      detail: { message },
      bubbles: true
    }))
  }

  /**
   * Build customer object for Paddle initialization
   * If user has existing paddle_customer_id: use { id: customer_id }
   * If user is new to Paddle: use { email: user_email }
   */
  private buildCustomerObject(): PaddleCustomer {
    if (this.hasCustomerIdValue && this.customerIdValue) {
      return { id: this.customerIdValue }
    }
    return { email: this.emailValue }
  }

  private async initializePaddle() {
    try {
      // Set environment based on token prefix
      // Sandbox tokens start with 'test_', production tokens start with 'live_'
      const environment = this.clientTokenValue.startsWith("test_") ? "sandbox" : "production"

      // Initialize Paddle using the official package
      // Note: Customer ID is captured server-side when creating the transaction (more reliable)
      this.paddle = await initializePaddle({
        environment,
        token: this.clientTokenValue,
        pwCustomer: this.buildCustomerObject(),
        eventCallback: (event: PaddleEventData) => {
          console.log("Paddle event:", event.name, event)
        }
      })
    } catch (error) {
      console.error('Failed to initialize Paddle:', error)
      this.showError('Payment system failed to load. Please refresh the page.')
    }
  }

  async openCheckout(event: Event) {
    event.preventDefault()

    // Check if Paddle is initialized
    if (!this.paddle) {
      errorHandler.showError("Checkout system not ready. Please refresh and try again.")
      return
    }

    try {
      // Create checkout transaction via backend
      const response = await fetch("/subscription", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken
        }
      })

      if (!response.ok) {
        throw new Error("Failed to initiate checkout")
      }

      const data = await response.json()

      // Open Paddle checkout overlay with successUrl
      // Paddle will handle the redirect after successful checkout
      this.paddle.Checkout.open({
        transactionId: data.transaction_id,
        settings: {
          successUrl: data.success_url
        }
      })
    } catch (error) {
      console.error("Error initiating checkout:", error)
      errorHandler.showError("Unable to start checkout. Please try again.")
    }
  }

  private get csrfToken(): string {
    const meta = document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement
    return meta ? meta.content : ""
  }
}
